import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Video, Image, Tag, Clock, Globe } from 'lucide-react';
import Modal from './Modal';
import { useAppContext } from '../../context/AppContext';
import { hasPermission, PERMISSIONS } from '../../utils/rbac';
import { explainerVideosAPI } from '../../services/api';
import { CONTENT_CATEGORIES, STATUS_VALUES } from '../../constants/enums';
import './VideoUploadModal.css';

const initialForm = {
  title: '',
  description: '',
  category: '',
  video_url: '',
  tags: '',
  status: STATUS_VALUES.ACTIVE,
  thumbnailFile: null,
};

const VideoUploadModal = () => {
  const { videoUploadModalOpen, closeVideoUploadModal, showNotification, currentUser } = useAppContext();
  const [form, setForm] = useState(initialForm);
  const [thumbName, setThumbName] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const thumbRef = useRef(null);
  const [categories, setCategories] = useState(CONTENT_CATEGORIES);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await explainerVideosAPI.getCategories();
        const fetchedCategories = response.data.data;
        console.log('Fetched video categories:', fetchedCategories);
        setCategories(fetchedCategories && fetchedCategories.length > 0 ? fetchedCategories : CONTENT_CATEGORIES);
      } catch (error) {
        console.error('Failed to fetch video categories:', error);
        console.log('Using fallback categories');
        setCategories(CONTENT_CATEGORIES);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (videoUploadModalOpen && !hasPermission(currentUser, PERMISSIONS.UPLOAD_EXPLAINER_VIDEOS)) {
      closeVideoUploadModal();
      showNotification('You do not have permission to upload explainer videos', 'error');
    }
  }, [videoUploadModalOpen, currentUser, closeVideoUploadModal, showNotification]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleThumbChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Store the file for later upload after video creation
      setForm((prev) => ({ ...prev, thumbnailFile: file }));
      setThumbName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare video data for API
      const videoData = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        video_url: form.video_url.trim(),
        status: form.status,
      };

      // Handle tags: convert comma-separated string to array
      if (form.tags.trim()) {
        const tagsArray = form.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
        videoData.tags = tagsArray;
      }

      // Create video via API
      const response = await explainerVideosAPI.createVideo(videoData);
      const createdVideo = response.data.data;
      const videoId = createdVideo.id;

      // If thumbnail file was selected, upload it
      if (form.thumbnailFile) {
        const thumbnailFormData = new FormData();
        thumbnailFormData.append('thumbnail', form.thumbnailFile);
        await explainerVideosAPI.uploadThumbnail(videoId, thumbnailFormData);
      }

      // Success
      setForm(initialForm);
      setThumbName('');
      setStep(1);
      // Reset file input
      if (thumbRef.current) {
        thumbRef.current.value = '';
      }
      closeVideoUploadModal();
      showNotification('Explainer video uploaded successfully!', 'success');
      // Trigger refresh of video list
      window.dispatchEvent(new CustomEvent('videoUploaded'));
    } catch (error) {
      console.error('Video upload failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload explainer video';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm(initialForm);
    setThumbName('');
    setStep(1);
    // Reset file input
    if (thumbRef.current) {
      thumbRef.current.value = '';
    }
    closeVideoUploadModal();
  };

  const isStep1Valid =
    form.title.trim() && form.category && form.status;

  const isStep2Valid = form.video_url.trim();

  return (
    <Modal
      isOpen={videoUploadModalOpen}
      onClose={handleClose}
      title="Upload Explainer Video"
      size="lg"
      footer={
        <div className="video-upload-modal__footer-actions">
          {step === 2 && (
            <button className="btn btn-outline" onClick={() => setStep(1)}>
              Back
            </button>
          )}
          <button className="btn btn-ghost" onClick={handleClose}>
            Cancel
          </button>
          {step === 1 ? (
            <button
              className="btn btn-primary"
              onClick={() => setStep(2)}
              disabled={!isStep1Valid}
            >
              Next: Video URL & Thumbnail
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading || !isStep2Valid}
            >
              {loading ? (
                <span className="video-upload-modal__spinner" />
              ) : (
                <>
                  <Upload size={15} />
                  Upload Video
                </>
              )}
            </button>
          )}
        </div>
      }
    >
      {/* Step Indicator */}
      <div className="video-upload-modal__steps">
        <div className={`video-upload-modal__step ${step >= 1 ? 'video-upload-modal__step--active' : ''}`}>
          <span className="video-upload-modal__step-num">1</span>
          <span>Video Details</span>
        </div>
        <div className="video-upload-modal__step-line" />
        <div className={`video-upload-modal__step ${step >= 2 ? 'video-upload-modal__step--active' : ''}`}>
          <span className="video-upload-modal__step-num">2</span>
          <span>Video URL & Thumbnail</span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="video-upload-modal__fields animate-fade-in">
            <div className="form-group">
              <label className="form-label">Video Title *</label>
              <input
                className="form-input"
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g., Safety Induction Overview"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Describe what this explainer video covers..."
                rows={4}
              />
            </div>

            <div className="video-upload-modal__row">
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select
                  className="form-select"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select category</option>
                  {(categories || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Status *</label>
                <select
                  className="form-select"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  required
                >
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Tag size={13} style={{ display: 'inline', marginRight: 4 }} />
                Tags (comma-separated)
              </label>
              <input
                className="form-input"
                type="text"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="e.g., safety, induction, onboarding"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="video-upload-modal__fields animate-fade-in">
            {/* Video URL */}
            <div className="video-upload-modal__file-zone">
              <div className="video-upload-modal__file-icon video-upload-modal__file-icon--url">
                <Video size={28} />
              </div>
              <div className="video-upload-modal__file-info">
                <span className="video-upload-modal__file-label">Video URL *</span>
                <input
                  className="form-input"
                  type="url"
                  name="video_url"
                  value={form.video_url}
                  onChange={handleChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
                <span className="video-upload-modal__file-hint">YouTube, Vimeo, or direct video file URL</span>
              </div>
            </div>

            {/* Thumbnail Upload */}
            <div className="video-upload-modal__file-zone" onClick={() => thumbRef.current?.click()}>
              <input
                ref={thumbRef}
                type="file"
                accept="image/*"
                onChange={handleThumbChange}
                style={{ display: 'none' }}
              />
              <div className="video-upload-modal__file-icon video-upload-modal__file-icon--img">
                <Image size={28} />
              </div>
              {thumbName ? (
                <div className="video-upload-modal__file-info">
                  <span className="video-upload-modal__file-name">{thumbName}</span>
                  <span className="video-upload-modal__file-change">Click to change</span>
                </div>
              ) : (
                <div className="video-upload-modal__file-info">
                  <span className="video-upload-modal__file-label">Upload Thumbnail (optional)</span>
                  <span className="video-upload-modal__file-hint">Optional. If not provided, a thumbnail will be generated from the video platform.</span>
                </div>
              )}
            </div>


            {/* Summary */}
            <div className="video-upload-modal__summary">
              <h4 className="video-upload-modal__summary-title">Video Summary</h4>
              <div className="video-upload-modal__summary-grid">
                <div><span>Title</span><strong>{form.title || '—'}</strong></div>
                <div><span>Category</span><strong>{form.category || '—'}</strong></div>
                <div><span>Status</span><strong>{form.status}</strong></div>
                <div><span>Tags</span><strong>{form.tags || '—'}</strong></div>
              </div>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default VideoUploadModal;